<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import { useTheme } from "../composables/useTheme";
import { useToast, type ToastKind } from "../composables/useToast";

const { dismissToast, toasts } = useToast();
const { theme } = useTheme();

const fullscreenElement = ref<Element | null>(null);
const teleportTarget = computed<Element>(() => fullscreenElement.value ?? document.body);

function _handleFullscreenChange() {
  fullscreenElement.value = document.fullscreenElement;
}

onMounted(() => {
  fullscreenElement.value = document.fullscreenElement;
  document.addEventListener("fullscreenchange", _handleFullscreenChange);
});

onBeforeUnmount(() => {
  document.removeEventListener("fullscreenchange", _handleFullscreenChange);
});

const KIND_ICON: Record<ToastKind, string> = {
  error: "heroicons:x-circle",
  info: "heroicons:information-circle",
  success: "heroicons:check-circle",
};

const KIND_TONE_CLASSES: Record<ToastKind, string> = {
  error: "bg-app-surface-raised ring-app-border-strong text-app-text",
  info: "bg-app-surface-raised ring-app-border-strong text-app-text",
  success: "bg-app-surface-raised ring-app-border-strong text-app-text",
};

const KIND_ICON_TONE: Record<ToastKind, string> = {
  error: "text-rose-400",
  info: "text-app-text-muted",
  success: "text-emerald-400",
};

const visibleToasts = computed(() => toasts.value);
</script>

<template>
  <Teleport :to="teleportTarget">
    <div :data-theme="theme"
      class="pointer-events-none fixed left-4 right-4 top-6 z-[10000] ml-auto flex max-w-sm flex-col gap-3 sm:left-auto sm:right-6">
      <TransitionGroup enter-active-class="transition-all duration-200" enter-from-class="translate-x-4 opacity-0"
        leave-active-class="transition-all duration-150" leave-to-class="translate-x-4 opacity-0" tag="div"
        class="flex flex-col gap-3">
        <div v-for="toast in visibleToasts" :key="toast.id" :class="[
          'pointer-events-auto flex items-center gap-3 rounded-2xl px-5 py-4 ring-1 backdrop-blur-xl shadow-app-large',
          KIND_TONE_CLASSES[toast.kind],
        ]" role="status">
          <Icon :icon="KIND_ICON[toast.kind]" :class="['h-5 w-5 shrink-0', KIND_ICON_TONE[toast.kind]]" />
          <div class="flex-1 min-w-0 text-sm leading-snug">
            <p v-if="toast.title" class="font-semibold text-app-text">{{ toast.title }}</p>
            <p :class="['text-app-text', toast.title ? 'mt-0.5 text-app-text-muted' : '']">{{ toast.message }}</p>
          </div>
          <button type="button"
            class="-mr-1 shrink-0 rounded-xl p-1.5 text-app-text-subtle transition hover:bg-app-surface-raised hover:text-app-text"
            aria-label="Schließen" @click="dismissToast(toast.id)">
            <Icon icon="heroicons:x-mark" class="h-4 w-4" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>
